import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBuilding,
  FaEye,
  FaPen,
  FaTrash,
  FaUserFriends,
  FaUsers,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import campaignAdvertisingService from '../../services/campaignAdvertisingService';
import campaignApplicationService from '../../services/campaignApplicationService';
// import AddInfluencerDetails from './AddInfluencerDetails';
// import AddAgencyDetails from './AddAgencyDetails';

const SELECTORS = [
  {
    key: 'all',
    label: 'All',
    helper: 'Show every campaign',
  },
  {
    key: 'influencer',
    label: 'Hire Influencer Directly',
    helper: 'Direct brand <> creator briefs',
    icon: <FaUserFriends className="text-orange-500" />,
  },
  {
    key: 'agency',
    label: 'Influencer Marketing Agencies',
    helper: 'Agency collaboration briefs',
    icon: <FaBuilding className="text-blue-500" />,
  },
];

const followerRangeMap = {
  Nano: { instagram: '1k - 10k', youtube: '1k - 5k' },
  Micro: { instagram: '10k - 50k', youtube: '5k - 20k' },
  Macro: { instagram: '50k - 500k', youtube: '20k - 100k' },
  Mega: { instagram: '500k - 1M', youtube: '100k - 1M' },
  Celeb: { instagram: '1M+', youtube: '1M+' },
};

const formatFollowerRangeDisplay = (ranges = []) => {
  if (!ranges.length) return '';
  return ranges
    .map((range) => {
      const detail = followerRangeMap[range];
      if (detail) {
        return `${range} (Instagram: ${detail.instagram} | YouTube: ${detail.youtube})`;
      }
      return range;
    })
    .join(', ');
};

const StatusToggle = ({ status, disabled, onToggle }) => {
  const isOn = status === 'active';

  return (
    <button
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-5 w-10 items-center rounded-full transition
        ${isOn ? 'bg-green-500' : 'bg-gray-300'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      title={disabled ? 'Toggle disabled' : 'Pause / Resume'}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition
          ${isOn ? 'translate-x-5' : 'translate-x-1'}
        `}
      />
    </button>
  );
};


const ViewAdvertisingCampaign = () => {
  const navigate = useNavigate();
  const [activeSelector, setActiveSelector] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [activeRecord, setActiveRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationCounts, setApplicationCounts] = useState({});
  const detailsCardRef = useRef(null);

  // Fetch campaign advertisings from backend
  useEffect(() => {
    const fetchCampaignAdvertisings = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = activeSelector !== 'all' ? { hireType: activeSelector } : {};
        const response = await campaignAdvertisingService.getAllCampaignAdvertisings(params);

        if (response.success && response.data) {
          // Transform backend data to match frontend structure
          // API may return campaign (embedded) or campaignId; brand/brandManager same
          const transformedCampaigns = response.data.map((item) => {
            const campaignSource = item.campaign || item.campaignId;
            // Get campaignId from top-level field or from embedded campaign object
            const campaignId = item.campaignId || item.campaign?._id || campaignSource?._id;
            const campaign = campaignSource
              ? { _id: campaignSource._id || campaignId, name: campaignSource.name || 'N/A', ...campaignSource }
              : { _id: campaignId, name: 'N/A' };
            const brandManagerSource = item.brandManager || item.brandManagerId;
            const brandManager = brandManagerSource && typeof brandManagerSource === 'object' && (brandManagerSource._id || brandManagerSource.name)
              ? {
                  _id: brandManagerSource._id,
                  name: brandManagerSource.name,
                  email: brandManagerSource.email,
                  phone: brandManagerSource.phone,
                  position: brandManagerSource.position,
                  linkedin: brandManagerSource.linkedin,
                }
              : null;
            return {
            id: item._id,
            _id: item._id,
            campaignId, // Store campaignId separately for API calls
            type: item.hireType,
            campaign,
            followerRangeDisplay: formatFollowerRangeDisplay(item.followerRange || []),
            formData: {
              brandName: item.brandName || item.brand?.brandName || '',
              budget: item.budget || '',
              numberOfCreators: item.numberOfCreators || '',
              platform: item.platform || [],
              category: item.category || item.brand?.category || [],
              gender: item.gender || '',
              location: item.location || [],
              language: item.language || [],
              followerRange: item.followerRange || [],
              deliverables: item.deliverables || '',
              agencyName: item.agencyName || '',
              brandManagerId: brandManagerSource?._id || (typeof brandManagerSource === 'string' ? brandManagerSource : null) || null,
              brandManager,
              contactDetails: {
                whatsapp: item.contactDetails?.whatsapp || '',
                email: item.contactDetails?.email || '',
                linkedin: item.contactDetails?.linkedin || '',
              },
            },
            requirement: item.requirement || '',
            status: item.status || 'draft',
            budgetMeta: item.budgetMeta,
            numberOfCreatorsMeta: item.numberOfCreatorsMeta,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          };
          });
          setCampaigns(transformedCampaigns);

          // Fetch application counts for each campaign advertising
          const counts = {};
          for (const campaign of transformedCampaigns) {
            try {
              const appResponse = await campaignApplicationService.getApplications(campaign._id);
              counts[campaign._id] = appResponse.count || 0;
            } catch (err) {
              console.error(`Error fetching applications for ${campaign._id}:`, err);
              counts[campaign._id] = 0;
            }
          }
          setApplicationCounts(counts);
        }
      } catch (err) {
        console.error('Error fetching campaign advertisings:', err);
        setError(err.message || 'Failed to fetch campaign advertisings');
        toast.error(err.message || 'Failed to load campaign advertisings');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignAdvertisings();
  }, [activeSelector]);

  const filteredCampaigns = useMemo(
    () =>
      activeSelector === 'all'
        ? campaigns
        : campaigns.filter((item) => item.type === activeSelector),
    [activeSelector, campaigns]
  );

  const handleDelete = async (record) => {
    if (!window.confirm('Delete this campaign draft?')) return;

    try {
      const campaignId = record.campaignId || record.campaign?._id;
      if (!campaignId) {
        toast.error('Campaign ID not found');
        return;
      }

      await campaignAdvertisingService.deleteCampaignAdvertising(campaignId, record._id || record.id);
      toast.success('Campaign advertising deleted successfully');

      // Remove from local state
      setCampaigns((prev) => prev.filter((item) => item.id !== record.id && item._id !== record._id));
      if (activeRecord?.id === record.id || activeRecord?._id === record._id) {
        setActiveRecord(null);
      }
    } catch (err) {
      console.error('Error deleting campaign advertising:', err);
      toast.error(err.message || 'Failed to delete campaign advertising');
    }
  };

  const handleView = (record) => {
    setActiveRecord(record);
  };

  // Scroll to details card when activeRecord changes
  useEffect(() => {
    if (activeRecord && detailsCardRef.current) {
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        detailsCardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 0);
    }
  }, [activeRecord]);

  const handleToggleStatus = async (record) => {
    try {
      const campaignId = record.campaignId || record.campaign?._id;
      if (!campaignId) {
        toast.error('Campaign ID not found');
        return;
      }

      const response =
        await campaignAdvertisingService.toggleAdvertisingStatus(
          campaignId,
          record._id
        );

      const updatedStatus = response.data.status;

      toast.success(
        updatedStatus === 'active'
          ? 'Campaign resumed successfully'
          : 'Campaign paused successfully'
      );

      // 🔁 Update local state (no refetch needed)
      setCampaigns((prev) =>
        prev.map((item) =>
          item._id === record._id
            ? { ...item, status: updatedStatus }
            : item
        )
      );
    } catch (error) {
      toast.error(error.message || 'Failed to update campaign status');
    }
  };

  const statusStyles = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    inactive: 'bg-gray-200 text-gray-700',
    expired: 'bg-red-100 text-red-700',
  };


  const handleEdit = (record) => {
    const route =
      record.type === 'influencer'
        ? '/company/dashboard/campaigns/advertising/add-details'
        : '/company/dashboard/campaigns/advertising/add-agency-details';

    navigate(route, {
      state: {
        campaign: record.campaign,
        requirement: record.requirement,
        formData: {
          ...record.formData,
          budgetMeta: record.budgetMeta,
          numberOfCreatorsMeta: record.numberOfCreatorsMeta,
        },
        advertisingId: record._id,
        status: record.status,
      },
    });
  };

  const handleViewApplicants = (record) => {
    navigate(`/company/dashboard/campaigns/advertising/${record._id}/applicants`);
  };

  const renderDetailsCard = () => {
    if (!activeRecord) return null;

    const fields = [
      ['Campaign', activeRecord.campaign?.name || '—'],
      ['Requirement', activeRecord.requirement || '—'],
      ['Brand', activeRecord.formData.brandName || '—'],
      ...(activeRecord.type === 'agency' && activeRecord.formData.agencyName
        ? [['Agency Name', activeRecord.formData.agencyName || '—']]
        : []),
      [
        'Brand Manager',
        activeRecord.formData.brandManager?.name ||
        // Fallback to raw id / string if populated object is not available
        activeRecord.formData.brandManagerId ||
        '—',
      ],
      ['Contact (WhatsApp)', activeRecord.formData.contactDetails?.whatsapp || '—'],
      ['Contact (Email)', activeRecord.formData.contactDetails?.email || '—'],
      ['Contact (LinkedIn)', activeRecord.formData.contactDetails?.linkedin || '—'],
      ['Budget', activeRecord.formData.budget || '—'],
      ['Creators', activeRecord.formData.numberOfCreators || '—'],
      ['Platforms', activeRecord.formData.platform?.join(', ') || '—'],
      ['Categories', activeRecord.formData.category?.join(', ') || '—'],
      ['Gender', activeRecord.formData.gender || '—'],
      ['Location', activeRecord.formData.location?.join(', ') || '—'],
      ['Language', activeRecord.formData.language?.join(', ') || '—'],
      ['Follower Range', formatFollowerRangeDisplay(activeRecord.formData.followerRange) || '—'],
      ['Deliverables', activeRecord.formData.deliverables || '—'],
    ];

    return (
      <div ref={detailsCardRef} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-semibold uppercase text-orange-500">
              Detailed View
            </p>
            <h2 className="text-xl font-semibold text-gray-900">
              {activeRecord.type === 'influencer'
                ? 'Hire Influencers'
                : 'Work with Agencies'}
            </h2>
          </div>
          <button
            onClick={() => setActiveRecord(null)}
            className="text-sm font-medium text-orange-500 hover:text-orange-600"
          >
            Close
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {fields.map(([label, value]) => (
            <div key={label}>
              <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                {label}
              </p>
              <p className="text-sm text-gray-900 mt-1 whitespace-pre-line break-words">
                {value}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-[calc(100dvh-80px)] bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p className="text-gray-600 text-xs sm:text-sm md:text-lg">Loading campaign advertisings...</p>
      </div>
    </div>
    );
  }

  if (error && campaigns.length === 0) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 md:px-4 py-4 md:py-6">
        <div className="mb-6">
          <p className="text-[10px] md:text-sm font-semibold uppercase text-orange-500">
            View advertising campaigns
          </p>
          <h1 className="text-base md:text-xl font-semibold text-gray-900">
            Manage ongoing campaign briefs
          </h1>
          <p className="text-[10px] md:text-sm text-gray-500 mt-1">
            Switch between creator-only briefs and agency collaborations, review
            essentials quickly, or jump into full detail forms without leaving
            this page.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 mb-6">
          {SELECTORS.map((selector) => {
            const isActive = selector.key === activeSelector;
            return (
              <button
                key={selector.key}
                onClick={() => setActiveSelector(selector.key)}
                className={`border rounded-2xl px-4 py-3 text-left transition shadow-sm ${isActive
                  ? 'border-orange-500 bg-white'
                  : 'border-gray-200 bg-white/80 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  {selector.icon}
                  <div>
                    <p
                      className={`lg:text-sm text-xs font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'
                        }`}
                    >
                      {selector.label}
                    </p>
                    <p className="lg:text-xs text-xs text-gray-500">{selector.helper}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {renderDetailsCard()}

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden mb-10">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <p className="lg:text-xs text-xs font-semibold uppercase text-gray-500">
                Campaign briefs
              </p>
              <h2 className="lg:text-lg text-base font-semibold text-gray-900">
                Quick overview
              </h2>
            </div>
            <span className="lg:text-sm text-xs font-medium text-gray-500">
              Showing {filteredCampaigns.length} campaign
              {filteredCampaigns.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gray-50 text-left lg:text-xs text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="py-3 px-6 whitespace-nowrap">Campaign</th>
                  <th className="py-3 px-6 whitespace-nowrap">Type</th>
                  <th className="py-3 px-6 whitespace-nowrap">Brand</th>
                  <th className="py-3 px-6 whitespace-nowrap">Budget</th>
                  <th className="py-3 px-6 whitespace-nowrap">Creators</th>
                  <th className="py-3 px-6 whitespace-nowrap">Influencer</th>
                  <th className="py-3 px-6 whitespace-nowrap">Status</th>
                  <th className="py-3 px-6 whitespace-nowrap text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 lg:text-sm text-xs text-gray-700">
                {filteredCampaigns.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50/80">
                    <td className="px-6 py-3">
                      <p className="lg:text-sm text-xs font-semibold text-gray-900">
                        {record.campaign?.name || 'N/A'}
                      </p>
                      <p className="lg:text-xs text-xs text-gray-500">{record._id?.substring(0, 8) || record.id?.substring(0, 8)}</p>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full lg:text-xs text-xs font-semibold ${record.type === 'influencer'
                          ? 'bg-orange-50 text-orange-600'
                          : 'bg-blue-50 text-blue-600'
                          }`}
                      >
                        {record.type === 'influencer'
                          ? 'Hire Influencers'
                          : 'Agencies'}
                      </span>
                    </td>
                    <td className="px-6 py-3">{record.formData.brandName || '—'}</td>
                    <td className="px-6 py-3">{record.formData.budget || '—'}</td>
                    <td className="px-6 py-3">{record.formData.numberOfCreators || '—'}</td>
                    <td className="px-6 py-3">
                      <button
                        onClick={() => handleViewApplicants(record)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium"
                        title="View applicants"
                      >
                        <FaUsers />
                        <span>{applicationCounts[record._id] || 0} Applied</span>
                      </button>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`px-2 py-1 rounded-full font-semibold ${statusStyles[record.status]
                          }`}
                      >
                        {record.status}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end space-x-3">

                        {/* ✅ STATUS TOGGLE */}
                        <StatusToggle
                          status={record.status}
                          disabled={['inactive', 'expired'].includes(record.status)}
                          onToggle={() => handleToggleStatus(record)}
                        />

                        <button
                          onClick={() => handleView(record)}
                          className="p-2 rounded-full bg-orange-50 text-orange-600 hover:bg-orange-100"
                          title="View"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleEdit(record)}
                          className="p-2 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100"
                          title="Edit"
                        >
                          <FaPen />
                        </button>
                        <button
                          onClick={() => handleDelete(record)}
                          className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCampaigns.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-8 text-center lg:text-sm text-xs text-gray-500"
                    >
                      No campaigns to display for this selection.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* {renderEmbeddedForms()} */}
      </div>
    </div>
  );
};

export default ViewAdvertisingCampaign;
